import * as i0 from '@angular/core';
import { Component, Input, ViewChild, Pipe, InjectionToken, PLATFORM_ID, Injectable, Inject, Directive, HostListener, inject, ContentChildren, signal, EventEmitter, HostBinding, Output, forwardRef, ChangeDetectionStrategy, ContentChild } from '@angular/core';
import * as i1$1 from '@angular/forms';
import { FormControl, FormRecord, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { isPlatformBrowser, NgTemplateOutlet, NgStyle, CommonModule } from '@angular/common';
import * as i2 from '@angular/cdk/drag-drop';
import { moveItemInArray, transferArrayItem, CdkDropListGroup, CdkDropList, CdkDrag, CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { Subject, fromEvent, tap, takeUntil, distinctUntilChanged, of, filter, take, map, BehaviorSubject, switchMap } from 'rxjs';
import { MarkerArea } from 'markerjs2';
import { MarkerView } from 'markerjs-live';
import { UUID } from 'angular2-uuid';
import * as i1 from '@studiohyperdrive/ngx-core';
import { simpleChangeHasChanged } from '@studiohyperdrive/ngx-core';
import { LiveAnnouncer } from '@angular/cdk/a11y';

/**
 * The layout item pairs with the `<ngx-configurable-layout>` container.
 * This component will in itself not be rendered. The key provided in this component
 * will need to be provided in the `[keys]` input or the `formControl` of the layout container
 * as well. The order in which they are provided there, will define the order in which
 * the items will be rendered.
 */
class NgxConfigurableLayoutItemComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: NgxConfigurableLayoutItemComponent, isStandalone: true, selector: "ngx-configurable-layout-item", inputs: { key: "key", label: "label" }, viewQueries: [{ propertyName: "template", first: true, predicate: ["contentTmpl"], descendants: true }], ngImport: i0, template: "<ng-template #contentTmpl>\n\t<ng-content></ng-content>\n</ng-template>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-configurable-layout-item', standalone: true, template: "<ng-template #contentTmpl>\n\t<ng-content></ng-content>\n</ng-template>\n" }]
        }], propDecorators: { key: [{
                type: Input,
                args: [{ required: true }]
            }], label: [{
                type: Input
            }], template: [{
                type: ViewChild,
                args: ['contentTmpl', { static: false }]
            }] } });

class NgxConfigurableLayoutItemSizePipe {
    /**
     * Returns the needed styling for the ngx-configurable-layout component
     *
     * @param keys - The keys used in the grid
     * @param showInactive - Whether we want to show inactive items
     * @param itemSize - The itemSize used by the layout
     */
    transform({ keys, showInactive, } = {
        keys: null,
        showInactive: true,
    }, itemSize) {
        // Iben: If non data source is provided or if the itemSize is 'fill',
        // we can use the default grid size which will result in filling up the available grid space.
        if (!keys || itemSize === 'fill') {
            return {};
        }
        // Iben: If itemSize is 'fit-content', the columns will be based on the content of the template
        if (itemSize === 'fit-content') {
            return {
                'grid-auto-columns': 'max-content',
            };
        }
        // Iben: If itemSize is 'equal', all items in the grid need to be of equal size.
        // For this, we grab the row with the largest amount of items, which will define the amount of columns
        const longestRow = Math.max(...[...keys].map((item) => {
            return item.filter((key) => {
                if (!showInactive) {
                    return key.isActive;
                }
                return true;
            }).length;
        }));
        return {
            'grid-template-columns': `repeat(${longestRow}, minmax(0, 1fr))`,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutItemSizePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutItemSizePipe, isStandalone: true, name: "ngxConfigurableLayoutItemSize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutItemSizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ngxConfigurableLayoutItemSize',
                    standalone: true,
                }]
        }] });

/**
 * A token to provide the necessary configuration to the directive
 */
const NgxDisplayContentConfigurationToken = new InjectionToken('NgxDisplayContentConfiguration');

/**
 * A service that provides the currently online status of the application
 */
class NgxOnlineService {
    constructor(platformId) {
        /**
         * A subject that emits whenever the application is on or offline
         */
        this.onlineSubject = new Subject();
        /**
         * A subject to handle the destroyed flow
         */
        this.onDestroySubject = new Subject();
        /**
         * An observable that emits whenever the application is on or offline
         */
        this.online$ = this.onlineSubject.asObservable();
        // Iben: When we're in the browser, listen to the online and offline status of the application
        if (isPlatformBrowser(platformId)) {
            // Iben: Handle the on and offline status of the application
            fromEvent(window, 'online')
                .pipe(tap(() => {
                this.onlineSubject.next(true);
            }), takeUntil(this.onDestroySubject.asObservable()))
                .subscribe();
            fromEvent(window, 'offline')
                .pipe(tap(() => {
                this.onlineSubject.next(false);
            }), takeUntil(this.onDestroySubject.asObservable()))
                .subscribe();
        }
    }
    ngOnDestroy() {
        // Iben: Complete the destroyed subject
        this.onDestroySubject.next();
        this.onDestroySubject.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxOnlineService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxOnlineService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxOnlineService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

/**
 * A service that serves as a wrapper for MarkerJs2 and MarkerJs-live
 *
 * https://markerjs.com/
 */
// TODO: Iben: To avoid essentially only testing a mocked version of this package, we need to test this through Cypress/Playwrite/... tests
class NgxImageMarkerService {
    constructor(windowService) {
        this.windowService = windowService;
        /**
         * A record with all markers
         */
        this.markers = {};
    }
    /**
     * Create an image with markers
     *
     * @param image - The image element we wish to draw markers on
     * @param rootTarget - The element we render the image in
     * @param configuration - The configuration we wish to pass to the image markers
     */
    createImageMarker(image, rootTarget, configuration) {
        // Iben: Return undefined if we're not in the browser
        if (!this.windowService.isBrowser) {
            return undefined;
        }
        // Iben: Create an id for the marker
        const id = UUID.UUID();
        // Iben: Depending on the provided mode, we create a edit view or a non editable view
        return configuration.mode === 'edit'
            ? this.createImageMakerEdit(id, image, rootTarget, configuration)
            : this.createReadonlyImageMarker(id, image, rootTarget, configuration);
    }
    /**
     * Creates a readonly marker view
     *
     * @param image - The image element we wish to draw markers on
     * @param rootTarget - The element we render the image in
     * @param configuration - The configuration we wish to pass to the image markers
     */
    createReadonlyImageMarker(id, image, rootTarget, configuration) {
        // Iben: Create the new marker view
        const marker = new MarkerView(image);
        // Iben: Set the root element so the marker layer is rendered in the same element
        marker.targetRoot = rootTarget;
        // Iben: Create the Angular marker view
        const result = {
            mode: 'view',
            close: (() => {
                // Iben: Remove the event listener and close the marker
                marker.removeEventListener('select', () => { });
                marker.close();
                // Iben: Remove the marker from the record
                this.markers[id] = undefined;
            }).bind(this),
            valueChanges: this.createMarkerClickedListener(marker),
        };
        // Iben: Create a clicked listener for the currently clicked item
        this.createMarkerClickedListener(marker);
        // Iben: If custom marker types were provided, set them as the available types
        if (configuration.markerTypes?.view) {
            marker.availableMarkerTypes = configuration.markerTypes.view;
        }
        // Iben: Show the marker once all settings are set
        marker.show(configuration.defaultState);
        // Iben: Add the marker to the record
        this.markers[id] = result;
        // Iben: Return the Angular view
        return result;
    }
    /**
     * Creates an editable marker view
     *
     * @param image - The image element we wish to draw markers on
     * @param rootTarget - The element we render the image in
     * @param configuration - The configuration we wish to pass to the image markers
     */
    createImageMakerEdit(id, image, rootTarget, configuration) {
        // Iben: Create a new marker view
        const marker = new MarkerArea(image);
        // Iben: Set the root element so the marker layer is rendered in the same element
        marker.targetRoot = rootTarget;
        // Iben: Create the Angular based view
        const result = {
            mode: 'edit',
            close: (() => {
                // Iben: Remove the event listener and close the marker
                marker.removeEventListener('statechange', () => { });
                marker.close();
                // Iben: Remove the marker from the record
                this.markers[id] = undefined;
            }).bind(this),
            valueChanges: this.createMarkerValueChanges(marker),
        };
        // Iben: Set the configuration settings
        // These can later on be extended when needed
        marker.uiStyleSettings.zoomButtonVisible = configuration.allowZoom;
        marker.uiStyleSettings.zoomOutButtonVisible = configuration.allowZoom;
        marker.zoomSteps = configuration.zoom?.levels || [1, 2, 3, 4];
        marker.zoomLevel = configuration.zoom?.current ?? 1;
        marker.uiStyleSettings.clearButtonVisible = configuration.allowClear;
        // Iben: Set the available marker types
        marker.availableMarkerTypes =
            configuration.markerTypes?.edit || marker.ALL_MARKER_TYPES;
        // Iben: Set the class names of the items
        marker.uiStyleSettings.notesAreaStyleClassName = 'ngx-image-marker-notes-area';
        marker.uiStyleSettings.toolbarStyleColorsClassName = 'ngx-image-marker-toolbar';
        marker.uiStyleSettings.toolboxStyleColorsClassName = 'ngx-image-marker-toolbox';
        marker.uiStyleSettings.toolbarButtonStyleColorsClassName =
            'ngx-image-marker-toolbar-button';
        marker.uiStyleSettings.toolbarActiveButtonStyleColorsClassName =
            'ngx-image-marker-toolbar-button-active';
        marker.uiStyleSettings.toolboxButtonStyleColorsClassName =
            'ngx-image-marker-toolbox-button';
        marker.uiStyleSettings.toolboxActiveButtonStyleColorsClassName =
            'ngx-image-marker-toolbox-button-active';
        // Iben: Show the marker once all settings are set
        marker.show();
        // Iben: If there was state before, set it accordingly
        if (configuration.defaultState) {
            marker.restoreState(configuration.defaultState);
        }
        // Iben: Add the marker to the record
        this.markers[id] = result;
        // Iben: Return the Angular view
        return result;
    }
    /**
     * Listen to the value changes in the editable view
     *
     * @param {MarkerArea} marker - The marker view
     */
    createMarkerValueChanges(marker) {
        // Iben: Setup valueChanges
        const valueChanges = new Subject();
        // Iben: Update the subject whenever a new item was added to the marker view
        marker.addEventListener('statechange', (event) => {
            valueChanges.next(event?.markerArea?.getState());
        });
        // Iben: Return changes observable
        // This Observable gets closed in the NgxImageMarkerComponent and therefor do not require a takeUntil here
        return valueChanges.asObservable();
    }
    /**
     * Listen to the marker clicks in the readonly view
     *
     * @param {MarkerArea} marker - The marker view
     */
    createMarkerClickedListener(marker) {
        // Iben: Setup valueChanges
        const valueChanges = new Subject();
        // Iben: Update the subject whenever a marker was clicked
        marker.addEventListener('select', (_, marker) => {
            valueChanges.next(marker);
        });
        // Iben: Return changes observable
        // This Observable gets closed in the NgxImageMarkerComponent and therefor do not require a takeUntil here
        return valueChanges.asObservable();
    }
    ngOnDestroy() {
        // Iben: Close all existing markers, removing the listeners where needed
        Object.values(this.markers)
            .filter(Boolean)
            .forEach((marker) => {
            marker.close();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxImageMarkerService, deps: [{ token: i1.NgxWindowService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxImageMarkerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxImageMarkerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.NgxWindowService }] });

/**
 * NgxDisplayContentDirective handles whether content should be shown based on a loading, error or offline condition by using provided default components or a provided override template
 */
class NgxDisplayContentDirective {
    /**
     * Renders the item or a default fallback based on the provided conditions
     */
    set displayContent(conditions) {
        // Iben: Update the conditions
        this.updateConditions(conditions);
        // Iben: Notify that the view needs to be updated
        this.updateViewSubject.next();
    }
    /**
     * Override the existing configuration with custom configuration
     */
    set displayContentConfiguration(configuration) {
        // Iben: Update the override configuration
        this.overrideConfiguration = configuration;
        // Iben: Notify that the view needs to be updated
        this.updateViewSubject.next();
    }
    constructor(elementRef, templateRef, cdRef, viewContainer, onlineService, configuration) {
        this.elementRef = elementRef;
        this.templateRef = templateRef;
        this.cdRef = cdRef;
        this.viewContainer = viewContainer;
        this.onlineService = onlineService;
        this.configuration = configuration;
        /**
         * A subject to handle the destroyed flow
         */
        this.onDestroySubject = new Subject();
        /**
         * A subject to know when we need to update the view
         */
        this.updateViewSubject = new Subject();
        /**
         * Holds an optional override configuration
         */
        this.overrideConfiguration = {};
        /**
         * Holds the conditions we use to render the component
         */
        this.conditions = {};
        /**
         * The aria-live label we wish to provide to the parent element. By default, this is 'polite'.
         *
         * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
         */
        this.displayContentAriaLive = 'polite';
        // Iben: If we want to listen to the online status, we set up a listener to the status of the application
        if (configuration.listenToOnlineStatus) {
            this.onlineService.online$
                .pipe(distinctUntilChanged(), tap((online) => {
                // Iben: Update the conditions with the new status
                this.updateConditions({ offline: !online });
                // Iben: Notify that the view needs to be updated
                this.updateViewSubject.next();
            }), takeUntil(this.onDestroySubject))
                .subscribe();
        }
    }
    ngAfterViewInit() {
        // Iben: Set the aria-live and aria-busy tag of the parent
        this.setAriaLiveTag(this.displayContentAriaLive);
        // Iben: Listen to whenever we need to update the view and act accordingly
        this.updateViewSubject
            .asObservable()
            .pipe(tap(() => {
            // Iben: Clear the current view container
            this.viewContainer.clear();
            // Iben: Update the busy tag
            this.setAriaBusyTag(this.conditions.loading);
            // Iben: If we're offline, we render the offline component or template
            if (this.conditions.offline) {
                this.renderTemplate('offline', this.configuration.components.offline);
                return;
            }
            // Iben: If we're loading, we render the loading component or template
            if (this.conditions.loading) {
                this.renderTemplate('loading', this.configuration.components.loading);
                return;
            }
            // Iben: If we've encountered an error, we render the error component or template
            if (this.conditions.error) {
                this.renderTemplate('error', this.configuration.components.error);
                return;
            }
            //Iben: Render the initial template
            this.renderInitialTemplate();
        }), 
        // Iben: We add a second tap here, so the detectChanges gets run after each outcome we did earlier
        tap(() => {
            // Iben: Detect the changes so this works with (nested) OnPush component
            this.cdRef.detectChanges();
        }), takeUntil(this.onDestroySubject.asObservable()))
            .subscribe();
        // Iben: Run the initial content check
        this.updateViewSubject.next();
    }
    ngOnDestroy() {
        this.onDestroySubject.next();
        this.onDestroySubject.complete();
    }
    /**
     * Renders the provided component or template
     *
     * @param  status - The status we're handling
     * @param  component - The component we're wishing to render
     */
    renderTemplate(status, component) {
        // Iben: If an override for this template exists, we render the override template and early exit
        if (this.overrideConfiguration[status]?.template) {
            this.viewContainer.createEmbeddedView(this.overrideConfiguration[status]?.template);
            return;
        }
        // Iben: If a component was provided, we render this component and pass along the provided data and early exit
        if (component) {
            // Iben: Render the component
            const componentRef = this.viewContainer.createComponent(component);
            // Iben: Pass the data
            componentRef.instance.data = this.overrideConfiguration[status]?.data;
            return;
        }
        // Iben: If we need to hide the template when no template was provided, we early exit
        if (this.configuration.hideWhenNoTemplateProvided) {
            return;
        }
        // Iben: In case we don't need to hide the initial template, we render it
        this.renderInitialTemplate();
    }
    /**
     * Renders the initial template
     */
    renderInitialTemplate() {
        this.viewContainer.createEmbeddedView(this.templateRef);
    }
    /**
     * Updates the conditions
     *
     * @param conditions - The conditions we want to merge with the current set
     */
    updateConditions(conditions) {
        // Iben: Update the conditions, but use a merge so that the online status can be preserved
        if (conditions) {
            this.conditions = {
                ...this.conditions,
                ...conditions,
            };
        }
    }
    /**
     * Sets the aria-live tag of the item
     * @param  value - The value we wish to set
     */
    setAriaLiveTag(value) {
        // Iben: Get the parent element and early exit if it isn't found
        const parentElement = this.elementRef.nativeElement.parentElement;
        if (!parentElement) {
            // Iben:
            console.error('NgxLayout: No parent element was found for NgxDisplayContentDirective. Because of that, the correct aria-live label could not be set.');
            return;
        }
        // Iben: If the value is assertive then we always set it, as it has the highest priority
        if (value === 'assertive') {
            parentElement.setAttribute('aria-live', value);
            return;
        }
        // Iben: Fetch the current aria-live label. If none were found, set it automatically
        const currentValue = parentElement.getAttribute('aria-live');
        if (!currentValue) {
            parentElement.setAttribute('aria-live', value);
        }
        // Iben: If the current value is assertive or if the values are the same, we early exit
        if (currentValue === 'assertive' || currentValue === value) {
            return;
        }
        // Iben: Set the value
        parentElement.setAttribute('aria-live', value);
    }
    /**
     * Sets the aria-busy tag of the item
     * @param  isLoading - The loading state of the item
     */
    setAriaBusyTag(isLoading) {
        this.elementRef.nativeElement.parentElement?.setAttribute('aria-busy', `${isLoading}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxDisplayContentDirective, deps: [{ token: i0.ElementRef }, { token: i0.TemplateRef }, { token: i0.ChangeDetectorRef }, { token: i0.ViewContainerRef }, { token: NgxOnlineService }, { token: NgxDisplayContentConfigurationToken }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxDisplayContentDirective, isStandalone: true, selector: "[displayContent]", inputs: { displayContent: "displayContent", displayContentConfiguration: "displayContentConfiguration", displayContentAriaLive: "displayContentAriaLive" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxDisplayContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[displayContent]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.TemplateRef }, { type: i0.ChangeDetectorRef }, { type: i0.ViewContainerRef }, { type: NgxOnlineService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NgxDisplayContentConfigurationToken]
                }] }], propDecorators: { displayContent: [{
                type: Input
            }], displayContentConfiguration: [{
                type: Input
            }], displayContentAriaLive: [{
                type: Input
            }] } });

/**
 * An abstract class to use when implementing custom fallbacks for the NgxDisplayContentDirective
 */
class NgxDisplayContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxDisplayContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxDisplayContentComponent, isStandalone: true, inputs: { data: "data" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxDisplayContentComponent, decorators: [{
            type: Directive
        }], propDecorators: { data: [{
                type: Input
            }] } });

//TODO: Iben: Move this copy to a shared lib once we have figured out how to handle that.
/**
 * An abstract directive used as a base to handle focussed base actions
 */
class NgxHasFocusDragAndDropAbstractDirective {
    /**
     * Set the hasFocus flag
     */
    setFocus() {
        this.hasFocus = true;
        if (this.onFocus) {
            this.onFocus();
        }
    }
    /**
     * Remove the hasFocus flag
     */
    removeFocus() {
        this.hasFocus = false;
        if (this.onBlur) {
            this.onBlur();
        }
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * Whether the current element is focussed
         */
        this.hasFocus = false;
    }
    focus() {
        this.elementRef.nativeElement.focus();
    }
    /**
     * Execute an action when the element has focussed
     *
     * @param action - The provided action
     */
    handleWhenFocussed(action) {
        if (this.hasFocus) {
            action();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxHasFocusDragAndDropAbstractDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxHasFocusDragAndDropAbstractDirective, isStandalone: true, host: { listeners: { "focus": "setFocus()", "blur": "removeFocus()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxHasFocusDragAndDropAbstractDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { setFocus: [{
                type: HostListener,
                args: ['focus']
            }], removeFocus: [{
                type: HostListener,
                args: ['blur']
            }] } });

/**
 * An abstract class to avoid circular dependency with the ItemDirective and the HostDirective
 */
class NgxAccessibleAbstractDragAndDropItemDirective extends NgxHasFocusDragAndDropAbstractDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleAbstractDragAndDropItemDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccessibleAbstractDragAndDropItemDirective, isStandalone: true, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleAbstractDragAndDropItemDirective, decorators: [{
            type: Directive
        }] });

const NgxAccessibleDragAndDropMessageRecords = {
    nl: {
        selected: '{{#item}} geselecteerd.',
        deselected: '{{#item}} gedeselecteerd.',
        reordered: '{{#item}} werd verplaatst van positie {{#from}} naar {{#to}}.',
        moved: '{{#item}} werd verplaatst van {{#from}} naar {{#to}}.',
        cancelled: 'Het verplaatsen van {{#item}} werd geannuleerd.',
        item: 'item',
        container: 'lijst',
        description: 'Om items in dit onderdeel te verplaatsen, navigeer naar een item via de Tab toets. Door de Enter of Space toets in te drukken selecteer je een item. Met de pijltjes toetsen verplaats je het item.',
    },
    en: {
        selected: '{{#item}} selected.',
        deselected: '{{#item}} deselected.',
        reordered: '{{#item}} was moved from position {{#from}} to {{#to}}.',
        moved: '{{#item}} was moved from {{#from}} to {{#to}}.',
        cancelled: 'Moving {{#item}} was cancelled.',
        item: 'item',
        container: 'list',
        description: 'To move items in this container, navigate to the item using the Tab key. By pressing Enter or Space you can select the item, which you can then move by using the Arrow keys.',
    },
    fr: {
        selected: '{{#item}} sélectionné.',
        deselected: '{{#item}} désélectionné.',
        reordered: '{{#item}} a été déplacé de la position {{#from}} à {{#to}}.',
        moved: '{{#item}} a été déplacé de {{#from}} à {{#to}}.',
        cancelled: 'Le déplacement de {{#item}} a été annulé.',
        item: 'article',
        container: 'liste',
        description: "Pour déplacer des éléments dans cette section, naviguez vers un élément à l'aide de la touche Tab. Appuyez sur la touche Entrée ou Espace pour sélectionner un élément. Utilisez les touches fléchées pour déplacer l'élément.",
    },
    es: {
        selected: '{{#item}} elegido.',
        deselected: '{{#item}} rechazado.',
        reordered: '{{#item}} fue reorganizado de la posición {{#from}} a {{#to}}.',
        moved: '{{#item}} fue trasladado de {{#from}} a {{#to}}.',
        cancelled: 'El desplazamiento de {{#item}} estuvo anulado.',
        item: 'objeto',
        container: 'lista',
        description: 'Para trasladar los objetos en esta sección, diríjase al objeto con la tecla Tab. Al tocar el botón Enter o el botón de Espacio puede seleccionar el objeto. Con las flechas puede trasladar el objeto.',
    },
    pt: {
        selected: '{{#item}} selecionado.',
        deselected: '{{#item}} desselecionado.',
        reordered: '{{#item}} foi movido da posição {{#from}} para {{#to}}.',
        moved: '{{#item}} foi movido de {{#from}} para {{#to}}.',
        cancelled: 'A movimentação de {{#item}} foi cancelada.',
        item: 'item',
        container: 'lista',
        description: 'Para mover itens nesta seção, navegue até um item usando a tecla Tab. Pressione Enter ou Barra de Espaço para selecionar um item. Use as teclas de seta para mover o item.',
    },
    tr: {
        selected: '{{#item}} seçildi.',
        deselected: '{{#item}} seçimi kaldırıldı.',
        reordered: '{{#item}} {{#from}} konumundan {{#to}} konumuna taşındı.',
        moved: '{{#item}} {{#from}} konumundan {{#to}} konumuna taşındı.',
        cancelled: '{{#item}} taşınması iptal edildi.',
        item: 'öğe',
        container: 'liste',
        description: 'Bu bölümdeki öğeleri taşımak için Tab tuşunu kullanarak bir öğeye gidin. Bir öğeyi seçmek için Enter veya Boşluk tuşuna basın. Öğeyi taşımak için ok tuşlarını kullanın.',
    },
    de: {
        selected: '{{#item}} ausgewählt.',
        deselected: '{{#item}} abgewählt.',
        reordered: '{{#item}} wurde von Position {{#from}} nach Position {{#to}} verschoben.',
        moved: '{{#item}} wurde von {{#from}} nach {{#to}} verschoben.',
        cancelled: 'Das Verschieben von {{#item}} wurde abgebrochen.',
        item: 'Eintrag',
        container: 'Liste',
        description: 'Um Einträge in diesem Teil zu verschieben, navigiere mit der Tab-Taste zu einem Eintrag. Mit Drücken der Eingabe- oder Leerzeichentaste wählst du einen Eintrag aus. Mit den Pfeiltasten verschiebst du den Eintrag.',
    },
    ku: {
        selected: '{{#item}} hate hilbijartin.',
        deselected: '{{#item}} hate rakirin.',
        reordered: '{{#item}} ji cîhê {{#from}} ve ji cîhê {{#to}} ve kişandin.',
        moved: '{{#item}} hate veguhastin ji {{#from}} ve {{#to}}.',
        cancelled: 'Kişandina {{#item}} hate betal kirin.',
        item: 'hêman',
        container: 'lista',
        description: 'Ji bo dikaribî hêmanên vê beşê bibî/bikşînî bibî devereka dî pêl tûşa TABê bike û here ser hêmanekî. Ji bo hêmanekî bineqînî pêl tûşa ENTER an jî tûşa SPACE yê bike. Ji bo hêmanekî bibî devereka dî tûşa OK bi kar bîne.',
    },
    // TODO: Iben: Get description translation
    // ru: {
    // 	selected: '{{#item}} выбрано.',
    // 	deselected: '{{#item}} снято выделение.',
    // 	reordered: '{{#item}} перенесен с позиции {{#from}} на {{#to}}.',
    // 	moved: '{{#item}} перенесен с {{#from}} на {{#to}}.',
    // 	cancelled: 'перемещение {{#item}} был отменен.',
    // 	item: 'элемент',
    // 	container: 'список',
    // 	description: 'TBD',
    // },
};

/**
 * Hides the element for users without assistive technologies
 *
 * @param {HTMLElement} element - A provided HTMLElement
 */
const hideElement = (element) => {
    element.style.position = 'absolute';
    element.style.width = '1px';
    element.style.height = '1px';
    element.style.marginTop = '-1px';
    element.style.overflow = 'hidden';
};

/**
 * An abstract service that is used to make drag and drop components accessible for assistive technologies
 */
class NgxAccessibleDragAndDropAbstractService {
    constructor() {
        /**
         * The live region service
         */
        this.liveRegionService = inject(LiveAnnouncer);
    }
    /**
     * Sets a message to the live region
     *
     * @param message - The provided message
     */
    setMessage(message) {
        // Iben: If no language was set, we early exit
        if (!this.currentLanguage) {
            console.error('NgxAccessibleDragAndDropAbstractService: No language was provided, so no message could be set.');
            return of();
        }
        // Iben: Take the current language to fetch the message
        return (typeof this.currentLanguage === 'string'
            ? of(this.currentLanguage)
            : this.currentLanguage).pipe(filter(Boolean), take(1), tap((currentLanguage) => {
            // Iben: Fetch the necessary data
            const { type, data } = message;
            let result = this.messageRecord[currentLanguage][type];
            // Iben: If no message was found, we early exit and throw an error
            if (!result) {
                console.error('NgxAccessibleDragAndDropAbstractService: No message for the corresponding drag and drop event was found.');
                return;
            }
            // Iben: Replace the necessary substrings
            if (type === 'selected' || type === 'deselected' || type === 'cancelled') {
                result = result.replace('{{#item}}', data.itemLabel || `${this.messageRecord[currentLanguage].item} ${data.item}`);
            }
            else if (type === 'moved') {
                result = result
                    .replace('{{#item}}', data.itemLabel ||
                    `${this.messageRecord[currentLanguage].item} ${data.item}`)
                    .replace(`{{#from}}`, data.fromLabel ||
                    `${this.messageRecord[currentLanguage].container} ${data.from}`)
                    .replace(`{{#to}}`, data.toLabel ||
                    `${this.messageRecord[currentLanguage].container} ${data.to}`);
            }
            else if (type === 'reordered') {
                result = result
                    .replace('{{#item}}', data.itemLabel ||
                    `${this.messageRecord[currentLanguage].item} ${data.item}`)
                    .replace(`{{#from}}`, data.from)
                    .replace(`{{#to}}`, data.to);
            }
            // Iben: Update the message in the live region
            this.liveRegionService.announce(result);
        }), map(() => null));
    }
    /**
     * Adds a description to the drag and drop host explaining how the drag and drop functions
     *
     * @param  parent - The drag and drop host
     * @param description - An optional description used to overwrite the default description
     */
    setDragAndDropDescription(parent, description) {
        // Iben: Create the description element and its id
        const element = document.createElement('p');
        const id = UUID.UUID();
        // Iben: Take the current language to fetch the message
        return (typeof this.currentLanguage === 'string'
            ? of(this.currentLanguage)
            : this.currentLanguage).pipe(tap((language) => {
            // Iben: Get the description text
            const text = description || this.messageRecord[language].description;
            // Iben: If no description was found, we early exit and throw an error
            if (!text) {
                console.error('NgxAccessibleDragAndDropAbstractService: No description for the drag and drop container was found.');
                return;
            }
            // Iben: Set the description and id of the element
            element.innerText = text;
            element.setAttribute('id', id);
            // Iben: Attach the element to the parent and update the aria id
            parent.appendChild(element);
            parent.setAttribute('aria-describedby', id);
            // Iben: Hide element
            hideElement(element);
        }), map(() => null));
    }
    /**
     * Returns the custom message record or the default when no custom record was provided
     */
    get messageRecord() {
        return this.customMessages || NgxAccessibleDragAndDropMessageRecords;
    }
}

/**
 * A directive to handle accessible drag and drop flows. This directive is meant to be placed on the drag and drop container(s).
 */
class NgxAccessibleDragAndDropContainerDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropContainerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccessibleDragAndDropContainerDirective, isStandalone: true, selector: "[ngxAccessibleDragAndDropContainer]", inputs: { index: ["ngxAccessibleDragAndDropContainerIndex", "index"], label: ["ngxAccessibleDragAndDropContainerLabel", "label"] }, exportAs: ["ngxAccessibleDragAndDropContainer"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxAccessibleDragAndDropContainer]',
                    exportAs: 'ngxAccessibleDragAndDropContainer',
                    standalone: true,
                }]
        }], propDecorators: { index: [{
                type: Input,
                args: [{ required: true, alias: 'ngxAccessibleDragAndDropContainerIndex' }]
            }], label: [{
                type: Input,
                args: [{ alias: 'ngxAccessibleDragAndDropContainerLabel' }]
            }] } });

/**
 * A directive to handle accessible drag and drop flows. This directive is meant to be placed on the host of the drag and drop container(s).
 */
class NgxAccessibleDragAndDropHostDirective {
    constructor(dragAndDropService, elementRef) {
        this.dragAndDropService = dragAndDropService;
        this.elementRef = elementRef;
        /**
         * A list of all the drag and drop items
         */
        this.items = {};
    }
    /**
     * Mark a specific drag and drop item as active
     *
     * @param  id - The id of the drag and drop item
     */
    markAsActive(id) {
        this.items[id].markAsActive();
    }
    /**
     * Returns the container based on the provided index
     *
     * @param index - The index of the container
     */
    getContainer(index) {
        return this.containers.find((container) => container.index === index);
    }
    ngAfterViewInit() {
        // Iben: Add the description tag
        this.dragAndDropService
            .setDragAndDropDescription(this.elementRef.nativeElement, this.description)
            .subscribe();
    }
    registerDragAndDropItem(item) {
        this.items[item.itemId] = item;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropHostDirective, deps: [{ token: NgxAccessibleDragAndDropAbstractService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccessibleDragAndDropHostDirective, isStandalone: true, selector: "[ngxAccessibleDragAndDropHost]", inputs: { description: ["ngxAccessibleDragAndDropHostDescription", "description"] }, queries: [{ propertyName: "containers", predicate: NgxAccessibleDragAndDropContainerDirective, descendants: true }], exportAs: ["ngxAccessibleDragAndDropHost"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxAccessibleDragAndDropHost]',
                    exportAs: 'ngxAccessibleDragAndDropHost',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: NgxAccessibleDragAndDropAbstractService }, { type: i0.ElementRef }], propDecorators: { containers: [{
                type: ContentChildren,
                args: [NgxAccessibleDragAndDropContainerDirective, { descendants: true }]
            }], description: [{
                type: Input,
                args: [{ alias: 'ngxAccessibleDragAndDropHostDescription' }]
            }] } });

/**
 * A directive to handle accessible drag and drop flows. This directive is meant to be placed on the item(s) of the drag and drop container(s).
 */
class NgxAccessibleDragAndDropItemDirective extends NgxAccessibleAbstractDragAndDropItemDirective {
    /**
     * Handle the selected state when pressing enter
     */
    onEnter() {
        this.handlePress();
    }
    /**
     * Handle the selected state when pressing space
     */
    onSpace() {
        this.handlePress();
    }
    /**
     * Handle the ArrowUp Press
     */
    onArrowUp(event) {
        this.moveItem('up', event);
    }
    /**
     * Handle the ArrowDown Press
     */
    onArrowDown(event) {
        this.moveItem('down', event);
    }
    /**
     * Handle the ArrowLeft Press
     */
    onArrowLeft(event) {
        this.moveItem('left', event);
    }
    /**
     * Handle the ArrowRight Press
     */
    onArrowRight(event) {
        this.moveItem('right', event);
    }
    /**
     * Whether the draggable item  is disabled
     */
    // eslint-disable-next-line @angular-eslint/no-input-rename
    set disabled(isDisabled) {
        this.tabIndex.set(isDisabled ? -1 : 0);
    }
    constructor(dragAndDropService, dropList, dropContainer, dropHost, elementRef) {
        super(elementRef);
        this.dragAndDropService = dragAndDropService;
        this.dropList = dropList;
        this.dropContainer = dropContainer;
        this.dropHost = dropHost;
        this.elementRef = elementRef;
        /**
         * The tab index of the item
         */
        this.tabIndex = signal(0);
        /**
         * The selected state of the item
         */
        this.isSelected = false;
        /**
         * Emits when the item has been moved through keyboard input
         */
        this.ngxAccessibleDragAndDropItemMove = new EventEmitter();
    }
    /**
     *  Marks the item as focussed and selected
     */
    markAsActive() {
        this.focus();
        this.isSelected = true;
    }
    /**
     * Deselects the current item if it's selected
     *
     */
    onBlur() {
        // Iben: Early exit if the item is not selected
        if (!this.isSelected) {
            return;
        }
        // Iben: Set the item as deselected
        this.isSelected = false;
        // Iben: Announce the item as deselected
        this.dragAndDropService
            .setMessage({
            type: 'deselected',
            data: { item: `${this.itemIndex}`, itemLabel: this.label || undefined },
        })
            .subscribe();
    }
    /**
     * Registers the item with the drop host
     */
    ngOnInit() {
        this.dropHost.registerDragAndDropItem(this);
    }
    /**
     * Handles the pressing of a button in the drag and drop host
     */
    handlePress() {
        this.handleWhenFocussed(() => {
            this.isSelected = !this.isSelected;
            this.dragAndDropService
                .setMessage({
                type: this.isSelected ? 'selected' : 'deselected',
                data: { item: `${this.itemIndex}`, itemLabel: this.label || undefined },
            })
                .subscribe();
        });
    }
    /**
     * Moves the item in the correct direction
     *
     * @param key - The pressed key
     * @param event - The keyboard event
     */
    moveItem(key, event) {
        if (!this.disabled && this.hasFocus && this.isSelected) {
            // Iben: Prevent the default action
            event.preventDefault();
            event.stopPropagation();
            // Iben: Set up the needed items
            let newIndex;
            let newContainer;
            const currentContainer = this.dropContainer.index;
            const isHorizontal = this.dropList.orientation === 'horizontal';
            const isUpOrDown = key === 'up' || key === 'down';
            // Iben: In this case we're changing the current container
            if ((isUpOrDown && isHorizontal) || (!isUpOrDown && !isHorizontal)) {
                newIndex = this.itemIndex;
                newContainer =
                    key === 'up' || key === 'left' ? currentContainer - 1 : currentContainer + 1;
                this.handleItemMove(newIndex, newContainer, 'moved');
            }
            // Iben: In this case, we're changing the order of the items
            if ((!isUpOrDown && isHorizontal) || (isUpOrDown && !isHorizontal)) {
                newIndex = key === 'up' || key === 'left' ? this.itemIndex - 1 : this.itemIndex + 1;
                newContainer = currentContainer;
                this.handleItemMove(newIndex, newContainer, 'reordered');
            }
        }
    }
    /**
     * Moves an item based on the provided container and index, and sends a message to the live region
     *
     * @private
     * @param newIndex - The new index of the item
     * @param newContainer - The container we wish to move the item to
     * @param  type - The type of movement we perform
     */
    handleItemMove(newIndex, newContainer, type) {
        // Iben: Check if the newContainer exits, if not early exit
        const targetContainer = this.dropHost.getContainer(newContainer);
        if (!targetContainer) {
            return;
        }
        // Iben: Emit the move event
        this.ngxAccessibleDragAndDropItemMove.emit({
            previousIndex: this.itemIndex,
            newIndex,
            previousContainer: this.dropContainer.index,
            newContainer,
        });
        // Iben: Set the message in the live region
        this.dragAndDropService
            .setMessage({
            type: type,
            data: {
                item: this.itemId,
                itemLabel: this.label || undefined,
                from: type === 'reordered'
                    ? `${this.itemIndex + 1}`
                    : `${this.dropContainer.index + 1}`,
                to: type === 'reordered' ? `${newIndex + 1}` : `${newContainer + 1}`,
                fromLabel: this.dropContainer.label || undefined,
                toLabel: targetContainer.label || undefined,
            },
        })
            .subscribe();
        // Iben: Set the focus and select the new item with the same id, using a setTimeOut so the correct item is rendered first
        setTimeout(() => {
            this.dropHost.markAsActive(this.itemId);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropItemDirective, deps: [{ token: NgxAccessibleDragAndDropAbstractService }, { token: i2.CdkDropList }, { token: NgxAccessibleDragAndDropContainerDirective }, { token: NgxAccessibleDragAndDropHostDirective }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccessibleDragAndDropItemDirective, isStandalone: true, selector: "[ngxAccessibleDragAndDropItem]", inputs: { itemIndex: ["ngxAccessibleDragAndDropItemIndex", "itemIndex"], itemId: ["ngxAccessibleDragAndDropItemId", "itemId"], label: ["ngxAccessibleDragAndDropLabel", "label"], disabled: ["ngxAccessibleDragAndDropDisabled", "disabled"] }, outputs: { ngxAccessibleDragAndDropItemMove: "ngxAccessibleDragAndDropItemMove" }, host: { listeners: { "keydown.Enter": "onEnter()", "keydown.Space": "onSpace()", "keydown.ArrowUp": "onArrowUp($event)", "keydown.ArrowDown": "onArrowDown($event)", "keydown.ArrowLeft": "onArrowLeft($event)", "keydown.ArrowRight": "onArrowRight($event)" }, properties: { "attr.tabIndex": "tabIndex()", "attr.aria-selected": "this.isSelected" } }, exportAs: ["ngxAccessibleDragAndDropItem"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccessibleDragAndDropItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxAccessibleDragAndDropItem]',
                    exportAs: 'ngxAccessibleDragAndDropItem',
                    standalone: true,
                    host: {
                        '[attr.tabIndex]': 'tabIndex()',
                    },
                }]
        }], ctorParameters: () => [{ type: NgxAccessibleDragAndDropAbstractService }, { type: i2.CdkDropList }, { type: NgxAccessibleDragAndDropContainerDirective }, { type: NgxAccessibleDragAndDropHostDirective }, { type: i0.ElementRef }], propDecorators: { isSelected: [{
                type: HostBinding,
                args: ['attr.aria-selected']
            }], onEnter: [{
                type: HostListener,
                args: ['keydown.Enter']
            }], onSpace: [{
                type: HostListener,
                args: ['keydown.Space']
            }], onArrowUp: [{
                type: HostListener,
                args: ['keydown.ArrowUp', ['$event']]
            }], onArrowDown: [{
                type: HostListener,
                args: ['keydown.ArrowDown', ['$event']]
            }], onArrowLeft: [{
                type: HostListener,
                args: ['keydown.ArrowLeft', ['$event']]
            }], onArrowRight: [{
                type: HostListener,
                args: ['keydown.ArrowRight', ['$event']]
            }], itemIndex: [{
                type: Input,
                args: [{ required: true, alias: 'ngxAccessibleDragAndDropItemIndex' }]
            }], itemId: [{
                type: Input,
                args: [{ required: true, alias: 'ngxAccessibleDragAndDropItemId' }]
            }], label: [{
                type: Input,
                args: [{ alias: 'ngxAccessibleDragAndDropLabel' }]
            }], disabled: [{
                type: Input,
                args: [{ alias: 'ngxAccessibleDragAndDropDisabled' }]
            }], ngxAccessibleDragAndDropItemMove: [{
                type: Output
            }] } });

/**
 * All the needed directives for the accessible drag and drop implementation
 */
const NgxAccessibleDragAndDrop = [
    NgxAccessibleDragAndDropItemDirective,
    NgxAccessibleDragAndDropContainerDirective,
    NgxAccessibleDragAndDropHostDirective,
];

/**
 * This component acts essentially as a layout wrapper. In combination with the
 * `<ngx-configurable-layout-item>` provided with a key as input, this component allows
 * you to dynamically change the order in which the items get rendered.
 *
 * The order of the items in the template does not matter, it gets set by the `[keys]` input.
 * You may also bind a `FormControl<string[][]>` to this component, which will allow you to reactively
 * change the order in which the elements get rendered. The control will always override the`[keys]` input.
 */
class NgxConfigurableLayoutComponent {
    constructor() {
        /**
         * A subject to mark the isActiveFormRecord as initialized
         */
        this.isActiveFormRecordInitializedSubject = new BehaviorSubject(false);
        // This component uses the internal implementation of the `ngx-forms` library.
        // Until we have moved to an NX workspace setup, we are unable to install the required
        //  dependencies to make the `ngx-forms` work.
        //
        // TODO: use the ngx-forms formAccessor instead of copying its internal way of working
        this.destroyedSubject = new Subject();
        this.destroyed$ = this.destroyedSubject.asObservable();
        this.form = new FormControl([]);
        this.isActiveFormRecord = new FormRecord({});
        /**
         * A record of the templates with the unique item `key` and its `templateRef`.
         */
        this.itemTemplateRecord = signal({});
        /**
         * A record of the label with the unique item `key` and its `label`.
         */
        this.itemLabelRecord = signal({});
        /**
         * Whether the inactive items should be visible in the layout.
         *
         * This property can only be used when the layoutType is set to `editable`.
         */
        this.showInactive = undefined;
        /**
         * Whether drag and drop is enabled for the layout.
         *
         * This property can only be used when the layoutType is set to `editable`
         */
        this.allowDragAndDrop = undefined;
        /**
         * Determines how much space each item takes in the row. By default, this is 'fill'
         *
         * fill - Will make the items take up the available space
         * fit-content - Will make the items take up the space of the provided template
         * equal - Will make the items take up equal amount of space throughout the grid
         */
        this.itemSize = 'fill';
        // Component methods
        // ==============================
        // TODO: use the ngx-forms formAccessor instead of copying its internal way of working
        this.onChanged = () => { };
        this.onTouched = () => { };
    }
    /**
     * The keys will determine the order of the `ngx-configurable-layout-item` layout items.
     *
     * This property can only be used when the layoutType  is set to `static`.
     */
    set keys(keys) {
        // Wouter: If no keys are provided, we prevent the patching of the control.
        if (!keys) {
            return;
        }
        // Wouter: Patch the provided keys onto the control.
        this.form.patchValue([...keys].map((row) => {
            return [...row].map((key) => ({ key, isActive: true }));
        }), { emitEvent: false });
    }
    // Lifecycle methods
    // ==============================
    ngOnInit() {
        // Iben: Listen to the form
        this.form.valueChanges
            .pipe(tap(() => {
            this.onChanged(this.form.value);
            this.onTouched();
        }), takeUntil(this.destroyed$))
            .subscribe();
        // Iben: Listen to the valueChanges of the isActiveFormRecord and update accordingly
        this.isActiveFormRecordInitializedSubject
            .pipe(filter(Boolean), take(1), switchMap(() => this.isActiveFormRecord.valueChanges), tap(() => {
            const result = [];
            // Iben: Loop over all keys and update the new isActive
            this.form.value.forEach((row) => {
                result.push([...row].map(({ key }) => {
                    return {
                        key,
                        isActive: this.isActiveFormRecord.value[key],
                    };
                }));
            });
            // Iben: Update the parent form
            this.form.setValue(result);
        }), takeUntil(this.destroyed$))
            .subscribe();
    }
    ngOnChanges(changes) {
        if ((changes.layoutType?.currentValue || this.layoutType) === 'static') {
            // Iben: If no keys are provided, we return an error, as without it, there nothing will be rendered and the layout will not work.
            if (!(changes.keys?.currentValue || this.keys)) {
                console.error('NgxLayout: The configurable layout was set to "static" but no 2D array of keys was provided. Provide an 2D array of keys to visualize the items. For more information, check the readme.');
            }
            // Iben: If either of the properties was set, we simply warn the user that these will have no effect as this will not influence the setup.
            if ((changes.allowDragAndDrop?.currentValue || this.allowDragAndDrop) !== undefined ||
                (changes.showInactive?.currentValue || this.showInactive) !== undefined) {
                console.warn('NgxLayout: The configurable layout was set to "static". Properties "allowDragAndDrop" and "showInactive" will have no effect. For more information, check the readme.');
            }
        }
        else {
            // Iben: If keys are provided, there's possible inconsistent behavior. We return an error so the user is notified.
            if (changes.keys?.currentValue || this.keys) {
                console.error('NgxLayout: The configurable layout was set to "editable". The property "keys" will have cause inconsistent behavior. For more information, check the readme.');
            }
        }
    }
    ngAfterContentChecked() {
        // Iben: Run with content check so that we can dynamically add templates/columns
        this.handleItemTemplates();
    }
    ngAfterViewInit() {
        // Femke: Run after view init so we have the initial templates
        this.handleItemTemplates();
    }
    ngOnDestroy() {
        this.destroyedSubject.next();
        this.destroyedSubject.complete();
    }
    writeValue(value) {
        // Iben: Update the inner form.
        this.form.setValue(value || [], { emitEvent: false });
        // Iben: Setup the isActive form
        (value || []).forEach((row) => {
            row.forEach(({ key, isActive }) => {
                this.isActiveFormRecord.setControl(key, new FormControl(isActive), {
                    emitEvent: false,
                });
            });
        });
        // Iben: Notify that the form has been initialized
        if (!this.isActiveFormRecordInitializedSubject.getValue()) {
            this.isActiveFormRecordInitializedSubject.next(true);
        }
    }
    registerOnChange(fn) {
        this.onChanged = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        isDisabled
            ? this.form.disable({ emitEvent: false })
            : this.form.enable({ emitEvent: false });
    }
    /**
     * Drag and drop an element in the grid
     *
     * @param {CdkDragDrop<string[]>} event - The grid element
     * @memberof NgxConfigurableLayoutComponent
     */
    drop(event) {
        // Iben: Fetch the arrays we need to update
        const startArrayIndex = parseInt(event.previousContainer.id.split('ngx-layout-row-')[1]);
        const endArrayIndex = parseInt(event.container.id.split('ngx-layout-row-')[1]);
        const formData = [...this.form.value];
        const startArray = formData[startArrayIndex];
        const endArray = formData[endArrayIndex];
        // Iben: If the drag and drop is within the same row, we move
        if (event.previousContainer === event.container) {
            moveItemInArray(endArray, event.previousIndex, event.currentIndex);
        }
        else {
            // Iben: If the drag and drop is over multiple rows, we transfer
            transferArrayItem(startArray, endArray, event.previousIndex, event.currentIndex);
            formData[startArrayIndex] = startArray;
        }
        formData[endArrayIndex] = endArray;
        // Iben: Update the form value
        this.form.setValue(formData);
    }
    /**
     * Moves an element based on its provided event
     *
     * @param event - The provided move event
     */
    move(event) {
        // Iben: Get the required data
        const formData = [...this.form.value];
        const startArray = formData[event.previousContainer];
        const endArray = formData[event.newContainer];
        // Iben: Early exit if the provided containers don't exist
        if (isNaN(event.previousContainer) || isNaN(event.newContainer)) {
            return;
        }
        // Iben: If the drag and drop is within the same row, we move
        if (event.previousContainer === event.newContainer) {
            moveItemInArray(endArray, event.previousIndex, event.newIndex);
        }
        else {
            // Iben: If the drag and drop is over multiple rows, we transfer
            transferArrayItem(startArray, endArray, event.previousIndex, event.newIndex);
            formData[event.previousContainer] = startArray;
        }
        formData[event.newContainer] = endArray;
        // Iben: Update the form value
        this.form.setValue(formData);
    }
    /**
     * The predicate we run before sorting
     *
     * @param  draggedElement - The dragged element
     * @param  list - The list into which we're dragging the element
     */
    beforeSort(_, draggedElement, list) {
        return this.dropPredicateHandler('sorting', draggedElement, list);
    }
    /**
     * The predicate we run before moving
     *
     * @param  draggedElement - The dragged element
     * @param  list - The list into which we're dragging the element
     */
    beforeDrop(draggedElement, list) {
        return this.dropPredicateHandler('moving', draggedElement, list);
    }
    /**
     * Handles the drop predicate
     *
     * @private
     * @param eventType - Whether we're moving or sorting
     * @param draggedElement - The element that's being dragged
     * @param list - The list into which we're dragging the element
     */
    dropPredicateHandler(eventType, draggedElement, list) {
        // Iben: If no predicate is passed, we always return true
        if (!this.dropPredicate) {
            return true;
        }
        // Iben: Fetch the index of the target
        const targetRowIndex = parseInt(list.id.split('ngx-layout-row-')[1]);
        // Iben: Fetch the current element
        const element = draggedElement.data;
        // Iben: Call the dropPredicate with the needed information
        return this.dropPredicate({
            eventType,
            currentGrid: [...this.form.value],
            element,
            showInactive: this.showInactive,
            targetRowIndex,
        });
    }
    // Component internal working
    /**
     * Update the item layout template order.
     */
    handleItemTemplates() {
        // Wouter: Clear the current item template record
        this.itemTemplateRecord.set({});
        this.itemLabelRecord.set({});
        Array.from(this.configurableItemTemplates).forEach((itemTemplate) => {
            const { key, template, label } = itemTemplate;
            // Wouter: Update the item template record with the unique column key and its template ref
            this.itemTemplateRecord.update((value) => ({ ...value, [key]: template }));
            this.itemLabelRecord.update((value) => ({
                ...value,
                [key]: label || key,
            }));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.0.3", type: NgxConfigurableLayoutComponent, isStandalone: true, selector: "ngx-configurable-layout", inputs: { layoutType: "layoutType", keys: "keys", showInactive: "showInactive", allowDragAndDrop: "allowDragAndDrop", itemSize: "itemSize", rowGap: "rowGap", dropPredicate: "dropPredicate", itemLabel: "itemLabel", rowLabel: "rowLabel", description: "description", columnGap: "columnGap" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => NgxConfigurableLayoutComponent),
                multi: true,
            },
        ], queries: [{ propertyName: "checkboxTemplate", first: true, predicate: ["checkboxTmpl"], descendants: true }, { propertyName: "configurableItemTemplates", predicate: NgxConfigurableLayoutItemComponent }], usesOnChanges: true, ngImport: i0, template: "<div\n\tclass=\"ngx-layout-grid\"\n\tcdkDropListGroup\n\tngxAccessibleDragAndDropHost\n\t[ngxAccessibleDragAndDropHostDescription]=\"description\"\n\t[class.ngx-layout-equal-size]=\"itemSize === 'equal'\"\n\t[style.row-gap]=\"rowGap\"\n\t[class.ngx-layout-grid-inactive-shown]=\"showInactive\"\n>\n\t@for (row of form.value; track row; let index = $index) {\n\t\t<ul\n\t\t\tclass=\"ngx-layout-row\"\n\t\t\tcdkDropList\n\t\t\tngxAccessibleDragAndDropContainer\n\t\t\tcdkDropListOrientation=\"horizontal\"\n\t\t\t[cdkDropListDisabled]=\"!allowDragAndDrop && !form.disabled\"\n\t\t\t[ngxAccessibleDragAndDropContainerIndex]=\"index\"\n\t\t\t[ngxAccessibleDragAndDropContainerLabel]=\"rowLabel ? rowLabel + ' ' + (index + 1) : ''\"\n\t\t\t[id]=\"'ngx-layout-row-' + index\"\n\t\t\t[ngStyle]=\"{ keys: form.value, showInactive } | ngxConfigurableLayoutItemSize: itemSize\"\n\t\t\t[style.column-gap]=\"columnGap\"\n\t\t\t[cdkDropListSortPredicate]=\"beforeSort.bind(this)\"\n\t\t\t[cdkDropListEnterPredicate]=\"beforeDrop.bind(this)\"\n\t\t\t(cdkDropListDropped)=\"drop($event)\"\n\t\t>\n\t\t\t@for (item of row; track item.key; let index = $index) {\n\t\t\t\t@if (itemTemplateRecord()[item.key]) {\n\t\t\t\t\t@if (showInactive || item.isActive) {\n\t\t\t\t\t\t<li\n\t\t\t\t\t\t\tclass=\"ngx-layout-item\"\n\t\t\t\t\t\t\tcdkDrag\n\t\t\t\t\t\t\tngxAccessibleDragAndDropItem\n\t\t\t\t\t\t\tcdkDragPreviewContainer=\"parent\"\n\t\t\t\t\t\t\t[class.ngx-layout-item-inactive]=\"!item.isActive\"\n\t\t\t\t\t\t\t[class.ngx-layout-item-disabled]=\"item.disabled\"\n\t\t\t\t\t\t\t[cdkDragData]=\"item\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropItemIndex]=\"index\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropItemId]=\"item.key\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropLabel]=\"\n\t\t\t\t\t\t\t\titemLabelRecord()[item.key] || itemLabel\n\t\t\t\t\t\t\t\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropDisabled]=\"\n\t\t\t\t\t\t\t\titem.disabled || (!allowDragAndDrop && !form.disabled)\n\t\t\t\t\t\t\t\"\n\t\t\t\t\t\t\t(ngxAccessibleDragAndDropItemMove)=\"move($event)\"\n\t\t\t\t\t\t>\n\t\t\t\t\t\t\t<ng-template\n\t\t\t\t\t\t\t\t[ngTemplateOutlet]=\"itemTemplateRecord()[item.key]\"\n\t\t\t\t\t\t\t></ng-template>\n\t\t\t\t\t\t\t@if (!form.disabled && showInactive && !item.disabled) {\n\t\t\t\t\t\t\t\t<ng-template\n\t\t\t\t\t\t\t\t\t[ngTemplateOutlet]=\"checkboxTemplate || defaultCheckBoxTmpl\"\n\t\t\t\t\t\t\t\t\t[ngTemplateOutletContext]=\"{\n\t\t\t\t\t\t\t\t\t\t$implicit: isActiveFormRecord.controls[item.key],\n\t\t\t\t\t\t\t\t\t}\"\n\t\t\t\t\t\t\t\t>\n\t\t\t\t\t\t\t\t</ng-template>\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t\t<div *cdkDragPlaceholder class=\"ngx-layout-drag-placeholder\"></div>\n\t\t\t\t\t\t</li>\n\t\t\t\t\t}\n\t\t\t\t}\n\t\t\t}\n\t\t</ul>\n\t}\n</div>\n\n<ng-template #defaultCheckBoxTmpl let-control>\n\t<input class=\"ngx-layout-item-toggle\" type=\"checkbox\" [formControl]=\"control\" />\n</ng-template>\n", styles: [":host .ngx-layout-grid{display:grid;grid-auto-flow:row}:host .ngx-layout-grid.ngx-layout-equal-size{grid-auto-rows:minmax(0,1fr)}:host .ngx-layout-row{display:grid;grid-auto-flow:column;height:100%;padding:unset;margin:unset}:host .ngx-layout-item{position:relative;padding:unset;margin:unset;min-height:max-content;list-style-type:none}:host .ngx-layout-item.cdk-drag:not(.cdk-drag-disabled){cursor:move}:host .ngx-layout-drag-placeholder{background:#ccc;border:dotted 3px #999;min-height:60px;transition:transform .25s cubic-bezier(0,0,.2,1)}:host .ngx-layout-item-toggle{position:absolute;top:5px;right:5px}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: CdkDropListGroup, selector: "[cdkDropListGroup]", inputs: ["cdkDropListGroupDisabled"], exportAs: ["cdkDropListGroup"] }, { kind: "directive", type: CdkDropList, selector: "[cdkDropList], cdk-drop-list", inputs: ["cdkDropListConnectedTo", "cdkDropListData", "cdkDropListOrientation", "id", "cdkDropListLockAxis", "cdkDropListDisabled", "cdkDropListSortingDisabled", "cdkDropListEnterPredicate", "cdkDropListSortPredicate", "cdkDropListAutoScrollDisabled", "cdkDropListAutoScrollStep", "cdkDropListElementContainer"], outputs: ["cdkDropListDropped", "cdkDropListEntered", "cdkDropListExited", "cdkDropListSorted"], exportAs: ["cdkDropList"] }, { kind: "directive", type: CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer", "cdkDragScale"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "directive", type: CdkDragPlaceholder, selector: "ng-template[cdkDragPlaceholder]", inputs: ["data"] }, { kind: "pipe", type: NgxConfigurableLayoutItemSizePipe, name: "ngxConfigurableLayoutItemSize" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1$1.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i1$1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: NgxAccessibleDragAndDropItemDirective, selector: "[ngxAccessibleDragAndDropItem]", inputs: ["ngxAccessibleDragAndDropItemIndex", "ngxAccessibleDragAndDropItemId", "ngxAccessibleDragAndDropLabel", "ngxAccessibleDragAndDropDisabled"], outputs: ["ngxAccessibleDragAndDropItemMove"], exportAs: ["ngxAccessibleDragAndDropItem"] }, { kind: "directive", type: NgxAccessibleDragAndDropContainerDirective, selector: "[ngxAccessibleDragAndDropContainer]", inputs: ["ngxAccessibleDragAndDropContainerIndex", "ngxAccessibleDragAndDropContainerLabel"], exportAs: ["ngxAccessibleDragAndDropContainer"] }, { kind: "directive", type: NgxAccessibleDragAndDropHostDirective, selector: "[ngxAccessibleDragAndDropHost]", inputs: ["ngxAccessibleDragAndDropHostDescription"], exportAs: ["ngxAccessibleDragAndDropHost"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxConfigurableLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-configurable-layout', imports: [
                        NgTemplateOutlet,
                        NgStyle,
                        CdkDropListGroup,
                        CdkDropList,
                        CdkDrag,
                        CdkDragPlaceholder,
                        NgxConfigurableLayoutItemSizePipe,
                        ReactiveFormsModule,
                        CommonModule,
                        NgxAccessibleDragAndDrop,
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => NgxConfigurableLayoutComponent),
                            multi: true,
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n\tclass=\"ngx-layout-grid\"\n\tcdkDropListGroup\n\tngxAccessibleDragAndDropHost\n\t[ngxAccessibleDragAndDropHostDescription]=\"description\"\n\t[class.ngx-layout-equal-size]=\"itemSize === 'equal'\"\n\t[style.row-gap]=\"rowGap\"\n\t[class.ngx-layout-grid-inactive-shown]=\"showInactive\"\n>\n\t@for (row of form.value; track row; let index = $index) {\n\t\t<ul\n\t\t\tclass=\"ngx-layout-row\"\n\t\t\tcdkDropList\n\t\t\tngxAccessibleDragAndDropContainer\n\t\t\tcdkDropListOrientation=\"horizontal\"\n\t\t\t[cdkDropListDisabled]=\"!allowDragAndDrop && !form.disabled\"\n\t\t\t[ngxAccessibleDragAndDropContainerIndex]=\"index\"\n\t\t\t[ngxAccessibleDragAndDropContainerLabel]=\"rowLabel ? rowLabel + ' ' + (index + 1) : ''\"\n\t\t\t[id]=\"'ngx-layout-row-' + index\"\n\t\t\t[ngStyle]=\"{ keys: form.value, showInactive } | ngxConfigurableLayoutItemSize: itemSize\"\n\t\t\t[style.column-gap]=\"columnGap\"\n\t\t\t[cdkDropListSortPredicate]=\"beforeSort.bind(this)\"\n\t\t\t[cdkDropListEnterPredicate]=\"beforeDrop.bind(this)\"\n\t\t\t(cdkDropListDropped)=\"drop($event)\"\n\t\t>\n\t\t\t@for (item of row; track item.key; let index = $index) {\n\t\t\t\t@if (itemTemplateRecord()[item.key]) {\n\t\t\t\t\t@if (showInactive || item.isActive) {\n\t\t\t\t\t\t<li\n\t\t\t\t\t\t\tclass=\"ngx-layout-item\"\n\t\t\t\t\t\t\tcdkDrag\n\t\t\t\t\t\t\tngxAccessibleDragAndDropItem\n\t\t\t\t\t\t\tcdkDragPreviewContainer=\"parent\"\n\t\t\t\t\t\t\t[class.ngx-layout-item-inactive]=\"!item.isActive\"\n\t\t\t\t\t\t\t[class.ngx-layout-item-disabled]=\"item.disabled\"\n\t\t\t\t\t\t\t[cdkDragData]=\"item\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropItemIndex]=\"index\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropItemId]=\"item.key\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropLabel]=\"\n\t\t\t\t\t\t\t\titemLabelRecord()[item.key] || itemLabel\n\t\t\t\t\t\t\t\"\n\t\t\t\t\t\t\t[ngxAccessibleDragAndDropDisabled]=\"\n\t\t\t\t\t\t\t\titem.disabled || (!allowDragAndDrop && !form.disabled)\n\t\t\t\t\t\t\t\"\n\t\t\t\t\t\t\t(ngxAccessibleDragAndDropItemMove)=\"move($event)\"\n\t\t\t\t\t\t>\n\t\t\t\t\t\t\t<ng-template\n\t\t\t\t\t\t\t\t[ngTemplateOutlet]=\"itemTemplateRecord()[item.key]\"\n\t\t\t\t\t\t\t></ng-template>\n\t\t\t\t\t\t\t@if (!form.disabled && showInactive && !item.disabled) {\n\t\t\t\t\t\t\t\t<ng-template\n\t\t\t\t\t\t\t\t\t[ngTemplateOutlet]=\"checkboxTemplate || defaultCheckBoxTmpl\"\n\t\t\t\t\t\t\t\t\t[ngTemplateOutletContext]=\"{\n\t\t\t\t\t\t\t\t\t\t$implicit: isActiveFormRecord.controls[item.key],\n\t\t\t\t\t\t\t\t\t}\"\n\t\t\t\t\t\t\t\t>\n\t\t\t\t\t\t\t\t</ng-template>\n\t\t\t\t\t\t\t}\n\t\t\t\t\t\t\t<div *cdkDragPlaceholder class=\"ngx-layout-drag-placeholder\"></div>\n\t\t\t\t\t\t</li>\n\t\t\t\t\t}\n\t\t\t\t}\n\t\t\t}\n\t\t</ul>\n\t}\n</div>\n\n<ng-template #defaultCheckBoxTmpl let-control>\n\t<input class=\"ngx-layout-item-toggle\" type=\"checkbox\" [formControl]=\"control\" />\n</ng-template>\n", styles: [":host .ngx-layout-grid{display:grid;grid-auto-flow:row}:host .ngx-layout-grid.ngx-layout-equal-size{grid-auto-rows:minmax(0,1fr)}:host .ngx-layout-row{display:grid;grid-auto-flow:column;height:100%;padding:unset;margin:unset}:host .ngx-layout-item{position:relative;padding:unset;margin:unset;min-height:max-content;list-style-type:none}:host .ngx-layout-item.cdk-drag:not(.cdk-drag-disabled){cursor:move}:host .ngx-layout-drag-placeholder{background:#ccc;border:dotted 3px #999;min-height:60px;transition:transform .25s cubic-bezier(0,0,.2,1)}:host .ngx-layout-item-toggle{position:absolute;top:5px;right:5px}\n"] }]
        }], propDecorators: { configurableItemTemplates: [{
                type: ContentChildren,
                args: [NgxConfigurableLayoutItemComponent]
            }], checkboxTemplate: [{
                type: ContentChild,
                args: ['checkboxTmpl']
            }], layoutType: [{
                type: Input,
                args: [{ required: true }]
            }], keys: [{
                type: Input
            }], showInactive: [{
                type: Input
            }], allowDragAndDrop: [{
                type: Input
            }], itemSize: [{
                type: Input
            }], rowGap: [{
                type: Input
            }], dropPredicate: [{
                type: Input
            }], itemLabel: [{
                type: Input
            }], rowLabel: [{
                type: Input
            }], description: [{
                type: Input
            }], columnGap: [{
                type: Input
            }] } });

/**
 * A WCAG/ARIA compliant implementation of the accordion pattern.
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */
class NgxAccordionComponent {
    constructor() {
        /**
         * A subject to hold a registered event
         */
        this.itemRegisteredSubject = new Subject();
        /**
         * A subject to hold the destroyed event
         */
        this.destroyedSubject = new Subject();
        /**
         * A list of all accordion items
         */
        this.items = [];
    }
    /**
     * Open the specific items in the accordion
     */
    set open(open) {
        this.itemRegisteredSubject
            .pipe(take(1), tap(() => {
            // Iben: Use a setTimeOut so we wait an extra tick
            setTimeout(() => {
                // Iben: Open all items
                if (open === 'all') {
                    this.items.forEach((item) => item.updateAccordionItemState(true));
                }
                else {
                    // Iben: Open specific items
                    const indexes = open === 'first' ? [0] : Array.isArray(open) ? open : [open];
                    indexes.forEach((index) => {
                        this.items[index]?.updateAccordionItemState(true);
                    });
                }
            });
        }))
            .subscribe();
    }
    /**
     * Register an accordion item to the container
     *
     * @param item - An accordion item
     */
    registerItem(item) {
        this.itemRegisteredSubject.next();
        this.items.push(item);
    }
    /**
     * Removes an accordion item from the container
     *
     * @param item - An accordion item
     */
    removeItem(item) {
        // Iben: Get the index of the item
        const index = this.items.findIndex(({ id }) => id === item.id);
        // Iben: If no item was found, we early exit
        if (index === undefined) {
            return;
        }
        // Iben: Remove the item
        this.items = [...this.items.slice(0, index), ...this.items.slice(index + 1)];
    }
    /**
     * Moves the focus to an accordion
     *
     * @param  id - The id of the current item
     * @param  direction - The direction we move in
     */
    moveFocus(id, direction) {
        // Iben: If we go to the first or last accordion, we don't need to find the index
        if (direction === 'first' || direction === 'last') {
            this.items[direction === 'first' ? 0 : this.items.length - 1].focus();
            return;
        }
        // Iben: Find the index and move to the next
        const index = this.items.findIndex((item) => id === item.id);
        this.items[direction === 'down' ? index + 1 : index - 1]?.focus();
    }
    /**
     * Handle the destroyed state
     */
    ngOnDestroy() {
        this.destroyedSubject.next();
        this.destroyedSubject.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccordionComponent, isStandalone: true, selector: "ngx-accordion", inputs: { open: "open" }, host: { attributes: { "role": "region" }, classAttribute: "ngx-accordion" }, ngImport: i0, template: '<ng-content/>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccordionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ngx-accordion',
                    template: '<ng-content/>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    host: {
                        class: 'ngx-accordion',
                        role: 'region',
                    },
                }]
        }], propDecorators: { open: [{
                type: Input
            }] } });

/**
 * A WCAG/ARIA compliant implementation of an item in the accordion pattern.
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */
class NgxAccordionItemComponent {
    /**
     * Moves the focus to the accordion item above the current one
     */
    arrowUp(event) {
        this.handleWhenFocussed(() => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.parent.moveFocus(this.id, 'up');
        });
    }
    /**
     * Moves the focus to the accordion item below the current one
     */
    arrowDown(event) {
        this.handleWhenFocussed(() => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.parent.moveFocus(this.id, 'down');
        });
    }
    /**
     * Moves the focus to the first accordion item
     */
    home() {
        this.handleWhenFocussed(() => {
            this.parent.moveFocus(this.id, 'first');
        });
    }
    /**
     * Moves the focus to the last accordion item
     */
    end() {
        this.handleWhenFocussed(() => {
            this.parent.moveFocus(this.id, 'last');
        });
    }
    constructor(parent, cdRef, renderer) {
        this.parent = parent;
        this.cdRef = cdRef;
        this.renderer = renderer;
        /**
         * Whether the accordion item is disabled
         */
        this.disabled = false;
        /**
         * The id of the accordion item
         */
        this.id = UUID.UUID();
        /**
         * Whether the accordion item is open
         */
        this.isOpen = false;
        /**
         * Whether the accordion item is focussed
         */
        this.hasFocus = false;
    }
    /**
     * Updates the current open/closed state of the accordion item, regardless of the disabled state
     */
    updateAccordionItemState(isOpen) {
        // Iben: Sets the item to open and updates the parent state
        this.isOpen = isOpen;
        // Iben: Trigger the visual changes
        this.cdRef.detectChanges();
    }
    /**
     * Set the focus on the summary item
     */
    focus() {
        this.summaryElement?.nativeElement.focus();
    }
    /**
     * Set the focus state of the accordion item
     *
     * @param hasFocus - Whether the item has focus
     */
    setFocus(hasFocus) {
        this.hasFocus = hasFocus;
    }
    /**
     * Register the item to its parent
     */
    ngOnInit() {
        this.parent.registerItem(this);
    }
    /**
     * Listen to the default HTML events of the details object
     */
    ngAfterViewInit() {
        // Iben: If for some reason no accordion item is found, we return
        if (!this.detailsElement?.nativeElement) {
            return;
        }
        // Iben: Prevent the accordion from being opened if it is disabled
        this.renderer.listen(this.detailsElement.nativeElement, 'click', (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        });
        // Iben: Listen to the open state of details and update the internal one
        this.renderer.listen(this.detailsElement.nativeElement, 'toggle', (event) => {
            this.updateAccordionItemState(event.newState === 'open');
        });
    }
    /**
     * Remove the item from its parent when destroyed
     */
    ngOnDestroy() {
        this.parent.removeItem(this);
    }
    /**
     * Only perform an action when the item has focus
     *
     * @param  action - The provided action
     */
    handleWhenFocussed(action) {
        // Iben: Early exit if there's no focus
        if (!this.hasFocus) {
            return;
        }
        // Iben: Perform the action
        action();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccordionItemComponent, deps: [{ token: NgxAccordionComponent }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: NgxAccordionItemComponent, isStandalone: true, selector: "ngx-accordion-item", inputs: { disabled: "disabled" }, host: { listeners: { "keydown.ArrowUp": "arrowUp($event)", "keydown.ArrowDown": "arrowDown($event)", "keydown.Home": "home()", "keydown.End": "end()" }, classAttribute: "ngx-accordion-item" }, queries: [{ propertyName: "contentTemplate", first: true, predicate: ["contentTmpl"], descendants: true }, { propertyName: "headerTemplate", first: true, predicate: ["headerTmpl"], descendants: true }], viewQueries: [{ propertyName: "detailsElement", first: true, predicate: ["details"], descendants: true }, { propertyName: "summaryElement", first: true, predicate: ["summary"], descendants: true }], ngImport: i0, template: "<details #details [open]=\"isOpen\">\n\t<summary\n\t\tclass=\"ngx-accordion-header\"\n\t\t#summary\n\t\t[class.is-disabled]=\"disabled\"\n\t\t[attr.disabled]=\"disabled\"\n\t\t[id]=\"id\"\n\t\t[attr.aria-controls]=\"'accordion-content-' + id\"\n\t\t[attr.aria-expanded]=\"isOpen\"\n\t\t(focus)=\"setFocus(true)\"\n\t\t(blur)=\"setFocus(false)\"\n\t>\n\t\t<ng-template\n\t\t\t[ngTemplateOutlet]=\"headerTemplate\"\n\t\t\t[ngTemplateOutletContext]=\"{ $implicit: isOpen }\"\n\t\t/>\n\t</summary>\n\n\t<div\n\t\tclass=\"ngx-accordion-content\"\n\t\trole=\"region\"\n\t\t[id]=\"'accordion-content-' + id\"\n\t\t[attr.aria-labelledby]=\"id\"\n\t>\n\t\t<ng-template\n\t\t\t[ngTemplateOutlet]=\"contentTemplate\"\n\t\t\t[ngTemplateOutletContext]=\"{ $implicit: isOpen }\"\n\t\t/>\n\t</div>\n</details>\n", styles: [":host details>summary{list-style:none}:host details>summary::-webkit-details-marker{display:none}:host summary{cursor:pointer}:host summary.is-disabled{cursor:not-allowed}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxAccordionItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-accordion-item', changeDetection: ChangeDetectionStrategy.OnPush, imports: [NgTemplateOutlet], host: {
                        class: 'ngx-accordion-item',
                    }, template: "<details #details [open]=\"isOpen\">\n\t<summary\n\t\tclass=\"ngx-accordion-header\"\n\t\t#summary\n\t\t[class.is-disabled]=\"disabled\"\n\t\t[attr.disabled]=\"disabled\"\n\t\t[id]=\"id\"\n\t\t[attr.aria-controls]=\"'accordion-content-' + id\"\n\t\t[attr.aria-expanded]=\"isOpen\"\n\t\t(focus)=\"setFocus(true)\"\n\t\t(blur)=\"setFocus(false)\"\n\t>\n\t\t<ng-template\n\t\t\t[ngTemplateOutlet]=\"headerTemplate\"\n\t\t\t[ngTemplateOutletContext]=\"{ $implicit: isOpen }\"\n\t\t/>\n\t</summary>\n\n\t<div\n\t\tclass=\"ngx-accordion-content\"\n\t\trole=\"region\"\n\t\t[id]=\"'accordion-content-' + id\"\n\t\t[attr.aria-labelledby]=\"id\"\n\t>\n\t\t<ng-template\n\t\t\t[ngTemplateOutlet]=\"contentTemplate\"\n\t\t\t[ngTemplateOutletContext]=\"{ $implicit: isOpen }\"\n\t\t/>\n\t</div>\n</details>\n", styles: [":host details>summary{list-style:none}:host details>summary::-webkit-details-marker{display:none}:host summary{cursor:pointer}:host summary.is-disabled{cursor:not-allowed}\n"] }]
        }], ctorParameters: () => [{ type: NgxAccordionComponent }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }], propDecorators: { detailsElement: [{
                type: ViewChild,
                args: ['details']
            }], summaryElement: [{
                type: ViewChild,
                args: ['summary']
            }], contentTemplate: [{
                type: ContentChild,
                args: ['contentTmpl']
            }], headerTemplate: [{
                type: ContentChild,
                args: ['headerTmpl']
            }], arrowUp: [{
                type: HostListener,
                args: ['keydown.ArrowUp', ['$event']]
            }], arrowDown: [{
                type: HostListener,
                args: ['keydown.ArrowDown', ['$event']]
            }], home: [{
                type: HostListener,
                args: ['keydown.Home']
            }], end: [{
                type: HostListener,
                args: ['keydown.End']
            }], disabled: [{
                type: Input
            }] } });

const NgxAccordion = [NgxAccordionComponent, NgxAccordionItemComponent];

/**
 * A component wrapper for MarkerJs views
 *
 * https://markerjs.com/
 */
// TODO: Iben: Once we figured out how we'll share the FormAccessors with other packages, we should make this a ControlValueAccessor
class NgxImageMarkerComponent {
    constructor(imageMarkerService, windowService, elementRef) {
        this.imageMarkerService = imageMarkerService;
        this.windowService = windowService;
        this.elementRef = elementRef;
        /**
         * A subject holding the destroy state of the marker
         */
        this.markerDestroyedSubject = new Subject();
        /**
         * Whether we can edit the view, by default this is true
         */
        this.canEdit = true;
        /**
         * Emits when the state has been updated
         */
        this.stateUpdated = new EventEmitter();
        /**
         * Emits when a marker is clicked when the view is in readonly mode
         */
        this.markerClicked = new EventEmitter();
    }
    ngAfterViewInit() {
        // Iben: Create the initial marker
        this.createMarker();
    }
    ngOnChanges(changes) {
        // Iben: If no marker exists or if the image has not rendered, early exit
        if (!this.currentMarker || !this.imageElement) {
            return;
        }
        // Iben: Check if there are changes to the state or the configuration when there is a marker
        const hasChanges = this.currentMarker &&
            (simpleChangeHasChanged(changes.startState) ||
                simpleChangeHasChanged(changes.canEdit) ||
                simpleChangeHasChanged(changes.markerTypes) ||
                simpleChangeHasChanged(changes.currentZoomLevel) ||
                simpleChangeHasChanged(changes.zoomLevels));
        // Iben: Recreate the marker whenever the configuration is adjusted
        if (!this.currentMarker || hasChanges) {
            this.createMarker();
        }
    }
    ngOnDestroy() {
        // Iben: Close the marker
        this.currentMarker.close();
        // Iben: Complete the destroy subject
        this.markerDestroyedSubject.next();
        this.markerDestroyedSubject.complete();
    }
    /**
     * Creates a MarkerJs view based on the provided configuration
     */
    createMarker() {
        // Iben: Only create the image when we're in the browser
        this.windowService.runInBrowser(() => {
            // Iben: Close the existing marker if needed
            if (this.currentMarker) {
                this.currentMarker.close();
                this.markerDestroyedSubject.next();
            }
            // Iben: Create a new marker view based on the provided configuration
            this.currentMarker = this.imageMarkerService.createImageMarker(this.imageElement.nativeElement, this.elementRef.nativeElement, {
                mode: this.canEdit ? 'edit' : 'view',
                allowZoom: true,
                defaultState: this.startState || undefined,
                markerTypes: this.markerTypes,
                zoom: this.currentZoomLevel !== undefined && this.zoomLevels
                    ? { current: this.currentZoomLevel, levels: this.zoomLevels }
                    : undefined,
            });
            // Iben: Listen to the valueChanges based on the provided type.
            if (this.currentMarker.mode === 'edit') {
                this.currentMarker.valueChanges
                    .pipe(tap((value) => {
                    this.stateUpdated.next(value);
                }), takeUntil(this.markerDestroyedSubject))
                    .subscribe();
            }
            else {
                this.currentMarker.valueChanges
                    .pipe(tap((value) => {
                    this.markerClicked.next(value);
                }), takeUntil(this.markerDestroyedSubject))
                    .subscribe();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxImageMarkerComponent, deps: [{ token: NgxImageMarkerService }, { token: i1.NgxWindowService }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.3", type: NgxImageMarkerComponent, isStandalone: true, selector: "ngx-image-marker", inputs: { image: "image", imageDescription: "imageDescription", startState: "startState", canEdit: "canEdit", currentZoomLevel: "currentZoomLevel", zoomLevels: "zoomLevels", markerTypes: "markerTypes" }, outputs: { stateUpdated: "stateUpdated", markerClicked: "markerClicked" }, host: { classAttribute: "ngx-image-marker" }, viewQueries: [{ propertyName: "imageElement", first: true, predicate: ["imageElement"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `<img
		class="ngx-image-marker-image"
		#imageElement
		[alt]="imageDescription"
		[src]="image"
	/>`, isInline: true, styles: [":host{display:block;position:relative;margin-bottom:40px}:host ::ng-deep [data-action=render],:host ::ng-deep [data-action=close]{display:none}:host ::ng-deep img{margin-top:0!important}:host ::ng-deep .ngx-image-marker-toolbox-button{fill:#fff}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: NgxImageMarkerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-image-marker', template: `<img
		class="ngx-image-marker-image"
		#imageElement
		[alt]="imageDescription"
		[src]="image"
	/>`, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'ngx-image-marker',
                    }, styles: [":host{display:block;position:relative;margin-bottom:40px}:host ::ng-deep [data-action=render],:host ::ng-deep [data-action=close]{display:none}:host ::ng-deep img{margin-top:0!important}:host ::ng-deep .ngx-image-marker-toolbox-button{fill:#fff}\n"] }]
        }], ctorParameters: () => [{ type: NgxImageMarkerService }, { type: i1.NgxWindowService }, { type: i0.ElementRef }], propDecorators: { imageElement: [{
                type: ViewChild,
                args: ['imageElement']
            }], image: [{
                type: Input,
                args: [{ required: true }]
            }], imageDescription: [{
                type: Input,
                args: [{ required: true }]
            }], startState: [{
                type: Input
            }], canEdit: [{
                type: Input
            }], currentZoomLevel: [{
                type: Input
            }], zoomLevels: [{
                type: Input
            }], markerTypes: [{
                type: Input
            }], stateUpdated: [{
                type: Output
            }], markerClicked: [{
                type: Output
            }] } });

/// components

/**
 * Provides the configuration for the NgxDisplayContent directive
 *
 * @param configuration - The required configuration
 */
const provideNgxDisplayContentConfiguration = (configuration) => {
    return {
        provide: NgxDisplayContentConfigurationToken,
        useValue: configuration,
    };
};

/**
 * Provides a custom implementation of the NgxDragAndDropService
 *
 * @param service - The custom implementation for the NgxDragAndDropService
 */
const provideNgxDragAndDropService = (service) => {
    return {
        provide: NgxAccessibleDragAndDropAbstractService,
        useClass: service,
    };
};

/*
 * Public API Surface of layout
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxAccessibleAbstractDragAndDropItemDirective, NgxAccessibleDragAndDrop, NgxAccessibleDragAndDropAbstractService, NgxAccordion, NgxAccordionComponent, NgxAccordionItemComponent, NgxConfigurableLayoutComponent, NgxConfigurableLayoutItemComponent, NgxDisplayContentComponent, NgxDisplayContentDirective, NgxHasFocusDragAndDropAbstractDirective, NgxImageMarkerComponent, NgxImageMarkerService, NgxOnlineService, provideNgxDisplayContentConfiguration, provideNgxDragAndDropService };
